import { AnyAction, createReducer, PayloadAction } from '@reduxjs/toolkit'
import {
  AssetsInfo,
  ReducerState,
  SessionType,
  StockWatchlistState,
} from '@interfaces'
import {
  clearWatchlistState,
  fetchCompanyDetail,
  fetchWatchlistStockState,
  resetExtendedHours,
  setExtendedHours,
  setWatchlistStockState,
  unsetWatchlistStock,
} from './thunk'

export type CompanyStocksState = ReducerState &
  StockWatchlistState & { asset: AssetsInfo } & {
    loadingChart: boolean
    selectedExtendedHours?: SessionType
  }

const initialState: CompanyStocksState = {
  asset: null,
  loading: true,
  loadingChart: true,
  error: null,
  isInWatchlist: false,
  isWatchlistStateLoading: false,
  errorWatchlist: null,
  selectedExtendedHours: SessionType.regular,
}

const clear = (action: AnyAction): boolean =>
  action.type === unsetWatchlistStock.fulfilled.type ||
  action.type === clearWatchlistState.type

export default createReducer(initialState, (builder) =>
  builder

    .addCase(fetchCompanyDetail.pending, (acc, { meta }) => {
      acc.loading = acc?.asset?.id !== meta?.arg?.company
      acc.loadingChart = acc?.asset?.id === meta?.arg?.company
      acc.error = null
    })
    .addCase(
      fetchCompanyDetail.fulfilled,
      (acc, { payload }: PayloadAction<AssetsInfo>) => {
        const sorted = payload?.chart?.sort(
          (a, b) =>
            new Date(a.dateTime).valueOf() - new Date(b.dateTime).valueOf(),
        )
        acc.asset = {
          ...payload,
          chart: sorted,
        }
        acc.loading = false
        acc.loadingChart = false
      },
    )
    .addCase(fetchCompanyDetail.rejected, (acc, { error }) => {
      acc.loading = false
      acc.loadingChart = false
      acc.error = error.message || ''
    })
    .addCase(
      fetchWatchlistStockState.fulfilled,
      (acc, action: PayloadAction<{ uuid: string; isFave?: boolean }>) => {
        const {
          payload: { isFave },
        } = action
        acc.isInWatchlist = isFave || false
        acc.isWatchlistStateLoading = false
        acc.errorWatchlist = null
      },
    )
    .addCase(fetchWatchlistStockState.pending, (acc) => {
      acc.isInWatchlist = false
      acc.isWatchlistStateLoading = true
      acc.errorWatchlist = null
    })
    .addCase(fetchWatchlistStockState.rejected, (acc, { error }) => {
      acc.isInWatchlist = false
      acc.isWatchlistStateLoading = false
      acc.errorWatchlist = error.message || ''
    })
    .addCase(setWatchlistStockState.fulfilled, (acc) => {
      acc.isInWatchlist = true
      acc.isWatchlistStateLoading = false
      acc.errorWatchlist = null
    })
    .addCase(setWatchlistStockState.pending, (acc) => {
      acc.isWatchlistStateLoading = true
      acc.errorWatchlist = null
    })
    .addCase(setWatchlistStockState.rejected, (acc, { error }) => {
      acc.isWatchlistStateLoading = false
      acc.errorWatchlist = error.message || ''
    })
    .addCase(unsetWatchlistStock.pending, (acc) => {
      acc.isWatchlistStateLoading = true
      acc.errorWatchlist = null
    })
    .addCase(unsetWatchlistStock.rejected, (acc, { error }) => {
      acc.isWatchlistStateLoading = false
      acc.errorWatchlist = error.message || ''
    })
    .addCase(setExtendedHours, (acc, { payload }) => {
      acc.selectedExtendedHours = payload
    })
    .addCase(resetExtendedHours, (acc) => {
      acc.selectedExtendedHours = SessionType.regular
    })
    .addMatcher(clear, (acc) => {
      acc.isInWatchlist = false
      acc.isWatchlistStateLoading = false
      acc.errorWatchlist = null
    }),
)
